// React
import { useCallback } from 'react'

// Components
import { BaseTableHeader, BaseTableFooter, BaseTableAction, BaseTableBody, BaseUploaderInline } from 'components'

// Antd
import { ColumnsType } from 'antd/es/table'
import { Button } from 'antd'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { ITableEventAttrs } from 'interfaces/table'
import { ISharedAnotherDocumentTableProps } from './interfaces'
import { ISharedAnotherDocument } from 'interfaces/shared'

// Mobx
import { observer } from 'mobx-react'

// Lodash
import isEmpty from 'lodash/isEmpty'

const SharedAnotherDocumentTable = observer(
  ({ data, loading, onChange, onAdd, onEdit, onDelete, onDownloadDocument, isReadOnly }: ISharedAnotherDocumentTableProps) => {
    // Hook
    const { t } = useTranslation()

    // Common State
    const columns: ColumnsType<ISharedAnotherDocument> = [
      {
        key: 'documentName',
        title: t('shared.anotherDocument.table.document'),
        dataIndex: 'documentName',
      },
      {
        key: 'userFileOriginalName',
        title: t('shared.anotherDocument.table.attachment'),
        dataIndex: 'userOriginalFileName',
        render: (_, record) => {
          return !isEmpty(record.userFileBlobId) && !isEmpty(record.userOriginalFileName) ? (
            <BaseUploaderInline
              originalFileName={record.userOriginalFileName}
              onDownload={() => onDownloadDocument && onDownloadDocument(record.userFileBlobId!)}
              loading={record.loading?.isDownloadLoading}
              isReadOnly
            />
          ) : (
            '-'
          )
        },
      },
      {
        key: 'x',
        title: t('app.table.action'),
        dataIndex: '',
        align: 'center',
        render: (_, record) => {
          return (
            <div className="flex justify-center items-center">
              <BaseTableAction
                isShow={false}
                onClickEdit={() => onEdit && onEdit(record.id!)}
                onClickDelete={() => onDelete && onDelete(record.id!)}
              />
            </div>
          )
        },
      },
    ]

    /**
     * @description Watch any change in table
     *
     * @param {ITableEventAttrs} event
     *
     * @return {void} void
     */
    const onChangeTable = useCallback(
      (event: ITableEventAttrs): void => {
        if (onChange) onChange(event)
      },
      [onChange],
    )

    /**
     * @description Filter columns
     *
     * @return {ColumnsType<ISharedAnotherDocument>} columns
     */
    const _columns = (): ColumnsType<ISharedAnotherDocument> => {
      const blacklistColumnKeys: string[] = []

      // Check if not readonly
      if (isReadOnly) blacklistColumnKeys.push('x')

      const _columns = !isEmpty(blacklistColumnKeys)
        ? columns.filter(column => blacklistColumnKeys.some(blacklistColumn => blacklistColumn !== column.key))
        : columns

      return _columns.map(column => {
        return {
          ...column,
          width: `${100 / columns.length}%`,
        }
      })
    }

    return (
      <>
        {/* Table Header */}
        <BaseTableHeader
          searchPlaceholder="myDocument.formPlaceholder.documentSearch"
          onChange={onChangeTable}
          loading={loading.isLoading}
          right={
            isReadOnly ? (
              <></>
            ) : (
              <div className="flex justify-end">
                <Button type="primary" className="custom-button button-table-header" loading={loading.isLoading} onClick={onAdd}>
                  {t('myDocument.uploadDocument')}
                </Button>
              </div>
            )
          }
        />

        {/* Table Body */}
        <BaseTableBody
          columns={_columns()}
          dataSource={[...data.list]}
          loading={loading.isLoading}
          rowKey="id"
          pagination={false}
          isBgPrimaryHeader
        />

        {/* Table Footer */}
        <BaseTableFooter onChange={onChangeTable} loading={loading.isLoading} data={data} />
      </>
    )
  },
)

export { SharedAnotherDocumentTable }
